import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { extractBeijingTime } from '../utils/timeUtils';
import {
  Code2,
  CheckCircle,
  XCircle,
  Award,
  FileCode,
  Target,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Terminal,
  Cpu,
  MemoryStick,
  Download,
  Image as ImageIcon,
  FileArchive
} from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import QuestionHtmlWithZoom from './QuestionHtmlWithZoom';

// Question 类型定义（与 ExamReviewPage 兼容）
interface Question {
  id: string;
  type: string;
  title: string;
  content?: string; // 题干内容（富文本）
  score: number;
  userAnswer?: string;
  referenceAnswer?: string;
  scoringPoints?: string;
}

interface ScratchResultDetailProps {
  question: Question;
  paperId: string;
  answer: string; // submissionId 或 sb3_file_url
  teacherFeedback?: { score?: number; feedback?: string }; // 教师评分数据
}

interface SubmissionDetail {
  id: string;
  sb3FileUrl: string | null;
  codeFileUrl: string | null;
  status: 'pending' | 'grading' | 'graded' | 'error';
  score?: number;
  maxScore?: number;
  executionTimeMs?: number;
  memoryUsedMb?: number;
  testResults?: TestResult[];
  submittedAt: string;
  feedback?: string;
  gradedBy?: 'ai' | 'teacher';
}

interface TestResult {
  testCaseId: string;
  passed: boolean;
  input?: string;
  expectedOutput?: string;
  actualOutput?: string;
  errorMessage?: string;
  executionTimeMs?: number;
}

interface QuestionDetail {
  id: string;
  content: string;
  referenceSolution?: string;
  scoringPoints?: string;
  timeLimitMs: number;
  memoryLimitMb: number;
  score: number;
  difficulty?: string;
}

// 空数据提示组件
function EmptyHint({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="text-center py-8 text-gray-400">
      <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <p className="text-gray-500">{text}</p>
    </div>
  );
}

export default function ScratchResultDetail({
  question,
  paperId,
  answer,
  teacherFeedback,
}: ScratchResultDetailProps) {
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    answer: true,
    grading: true,
    question: true,
    reference: true,
    rules: true,
  });

  useEffect(() => {
    loadData();
  }, [answer, question.id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadSubmission(), loadQuestionDetail()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmission = async () => {
    // 如果没有答案，清空提交状态并直接返回
    if (!answer || answer.trim() === '') {
      setSubmission(null);
      return;
    }

    // 判断 answer 是 submissionId 还是直接的文件URL
    const isSubmissionId = answer.length === 36 && answer.includes('-');

    if (isSubmissionId) {
      // 通过 submissionId 查询
      const { data, error } = await supabase
        .from('scratch_submissions')
        .select('*')
        .eq('id', answer)
        .maybeSingle();

      if (error) throw new Error(`加载提交信息失败: ${error.message}`);

      if (data) {
        // 从 result_details 中提取执行详情
        const resultDetails = data.result_details as Record<string, any> | null;

        // 处理 test_case_results
        let parsedTestResults: TestResult[] | undefined;
        const testResultsData = data.test_case_results || resultDetails?.test_case_results;
        if (testResultsData) {
          if (typeof testResultsData === 'string') {
            try {
              parsedTestResults = JSON.parse(testResultsData);
            } catch {
              parsedTestResults = [];
            }
          } else if (Array.isArray(testResultsData)) {
            parsedTestResults = testResultsData as unknown as TestResult[];
          }
        }

        // 判断评分来源：如果有 ai_score 认为是 AI 评分，否则如果 graded_at 存在但无 ai_score 可能是教师评分
        let gradedBy: 'ai' | 'teacher' | undefined = undefined;
        let finalScore = data.total_score ?? data.ai_score ?? undefined;
        let finalFeedback = data.ai_feedback || undefined;

        if (data.graded_at) {
          gradedBy = data.ai_score != null ? 'ai' : 'teacher';
        }

        // 使用通过 props 传递的教师评分数据
        if (teacherFeedback) {
          if (teacherFeedback.score !== undefined) {
            finalScore = teacherFeedback.score;
            gradedBy = 'teacher';
          }
          if (teacherFeedback.feedback) {
            finalFeedback = teacherFeedback.feedback;
          }
        }

        setSubmission({
          id: data.id,
          sb3FileUrl: data.sb3_file_url || null,
          codeFileUrl: null, // scratch_submissions 没有 code_file_url
          status: (data.status as 'pending' | 'grading' | 'graded' | 'error') || 'pending',
          score: finalScore,
          maxScore: question.score,
          executionTimeMs: undefined, // scratch 没有执行时间
          memoryUsedMb: undefined, // scratch 没有内存使用
          testResults: parsedTestResults,
          submittedAt: data.submitted_at || data.created_at || '',
          feedback: finalFeedback,
          gradedBy: gradedBy,
        });
      }
    } else {
      // answer 是直接的 sb3 文件 URL（学员上传了文件但未经过系统评分）
      setSubmission({
        id: 'direct-url',
        sb3FileUrl: answer,
        codeFileUrl: null,
        status: 'pending',
        submittedAt: new Date().toISOString(),
      });
    }
  };

  const loadQuestionDetail = async () => {
    // 从 questions 表获取题目详情
    const { data, error } = await supabase
      .from('questions')
      .select('content, explanation, reference_answer, scoring_points, score')
      .eq('id', question.id)
      .single();

    if (error) {
      // 使用默认数据，优先使用 question.questionStem（题干富文本），回退到 question.content 或 question.title
      setQuestionDetail({
        id: question.id,
        content: question.questionStem || question.content || question.title || '',
        referenceSolution: question.referenceAnswer,
        scoringPoints: question.scoringPoints,
        timeLimitMs: 1000,
        memoryLimitMb: 64,
        score: question.score,
      });
      return;
    }

    if (data) {
      setQuestionDetail({
        id: question.id,
        content: data.content || question.questionStem || question.content || question.title || '',
        referenceSolution: data.reference_answer || undefined,
        scoringPoints: data.scoring_points || undefined,
        timeLimitMs: 1000,
        memoryLimitMb: 64,
        score: data.score || question.score,
      });
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  };

  // 统一评分判断逻辑
  const isGraded = teacherFeedback?.score != null || (submission?.status === 'graded' && submission?.score != null);
  const gradedBy = teacherFeedback?.score != null ? 'teacher' : submission?.gradedBy;
  const displayScore = teacherFeedback?.score ?? submission?.score ?? 0;
  const maxScore = submission?.maxScore ?? question.score;
  const displayFeedback = teacherFeedback?.feedback ?? submission?.feedback ?? '';

  // 获取文件名
  const getFileName = (url?: string | null) => {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">加载中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg p-4 border border-red-200 flex items-center gap-2 text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  // 判断各区域是否有数据
  const hasAnswerData = !!submission?.sb3FileUrl || !!submission?.codeFileUrl;
  const hasReferenceData = !!(questionDetail?.referenceSolution || question.referenceAnswer);
  const hasRulesData = !!(questionDetail?.scoringPoints || question.scoringPoints);

  return (
    <div className="space-y-4">
      {/* 你的答案区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('answer')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">你的答案</span>
            {submission && (
              <span className="text-sm text-gray-500">
                提交时间: {formatDateTime(submission.submittedAt)}
              </span>
            )}
          </div>
          {expandedSections.answer ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.answer && (
          <div className="p-4">
            {hasAnswerData ? (
              <div className="space-y-4">
                {/* SB3 文件下载 */}
                {submission?.sb3FileUrl && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileArchive className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-900">Scratch 项目文件</p>
                          <p className="text-sm text-gray-500">{getFileName(submission.sb3FileUrl)}</p>
                        </div>
                      </div>
                      <a
                        href={submission.sb3FileUrl}
                        download
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        下载
                      </a>
                    </div>
                  </div>
                )}
                
                {/* 代码文件下载 */}
                {submission?.codeFileUrl && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Code2 className="w-8 h-8 text-gray-600" />
                        <div>
                          <p className="font-medium text-gray-900">代码文件</p>
                          <p className="text-sm text-gray-500">{getFileName(submission.codeFileUrl)}</p>
                        </div>
                      </div>
                      <a
                        href={submission.codeFileUrl}
                        download
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        下载
                      </a>
                    </div>
                  </div>
                )}

                {/* 预览提示 */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <ImageIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">项目预览</p>
                      <p className="text-sm text-amber-700 mt-1">
                        请下载 SB3 文件后在 Scratch 编辑器中打开查看项目详情。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyHint icon={FileArchive} text="未提交答案" />
            )}
          </div>
        )}
      </motion.div>

      {/* 评分结果区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('grading')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-gray-900">评分结果</span>
            {isGraded && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                gradedBy === 'teacher'
                  ? 'bg-blue-100 text-blue-700'
                  : gradedBy === 'ai'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-700'
              }`}>
                {gradedBy === 'teacher' ? '教师评分' : gradedBy === 'ai' ? 'AI评分' : '已评分'}
              </span>
            )}
          </div>
          {expandedSections.grading ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.grading && (
          <div className="p-4">
            {submission ? (
              <div className="space-y-3">
                {isGraded ? (
                  <>
                    {/* 分数展示区 */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <div className="text-xs text-blue-600 mb-1">本题分值</div>
                        <div className="text-2xl font-bold text-blue-700">{maxScore}<span className="text-sm font-normal ml-1">分</span></div>
                      </div>
                      <div className={`bg-gradient-to-br rounded-lg p-4 border ${
                        displayScore > 0
                          ? 'from-green-50 to-green-100 border-green-200'
                          : 'from-red-50 to-red-100 border-red-200'
                      }`}>
                        <div className={`text-xs mb-1 ${displayScore > 0 ? 'text-green-600' : 'text-red-600'}`}>你的得分</div>
                        <div className={`text-2xl font-bold ${displayScore > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {displayScore}<span className="text-sm font-normal ml-1">分</span>
                        </div>
                      </div>
                    </div>
                    {/* 评语 */}
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-gray-600 w-16 flex-shrink-0">评语：</span>
                      <span className="text-sm text-gray-800 whitespace-pre-wrap">
                        {displayFeedback || '暂无评语'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* 分数展示区 - 待批阅 */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <div className="text-xs text-blue-600 mb-1">本题分值</div>
                        <div className="text-2xl font-bold text-blue-700">{maxScore}<span className="text-sm font-normal ml-1">分</span></div>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                        <div className="text-xs text-amber-600 mb-1">你的得分</div>
                        <div className="text-2xl font-bold text-amber-700">待批阅</div>
                      </div>
                    </div>
                    {/* 评语 */}
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-gray-600 w-16 flex-shrink-0">评语：</span>
                      <span className="text-sm text-gray-500">暂无评语</span>
                    </div>
                  </>
                )}
              </div>
            ) : teacherFeedback ? (
              <>
                {/* 有教师评分但无提交记录 */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="text-xs text-blue-600 mb-1">本题分值</div>
                    <div className="text-2xl font-bold text-blue-700">{maxScore}<span className="text-sm font-normal ml-1">分</span></div>
                  </div>
                  <div className={`bg-gradient-to-br rounded-lg p-4 border ${
                    teacherFeedback.score > 0
                      ? 'from-green-50 to-green-100 border-green-200'
                      : 'from-red-50 to-red-100 border-red-200'
                  }`}>
                    <div className={`text-xs mb-1 ${teacherFeedback.score > 0 ? 'text-green-600' : 'text-red-600'}`}>你的得分</div>
                    <div className={`text-2xl font-bold ${teacherFeedback.score > 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {teacherFeedback.score ?? 0}<span className="text-sm font-normal ml-1">分</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-gray-600 w-16 flex-shrink-0">评语：</span>
                  <span className="text-sm text-gray-800 whitespace-pre-wrap">{teacherFeedback.feedback || '暂无评语'}</span>
                </div>
              </>
            ) : (
              <>
                {/* 未提交 */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-16">评分：</span>
                  <span className="font-bold text-red-600">0/{maxScore}分</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-gray-600 w-16">评语：</span>
                  <span className="text-sm text-gray-600">此题未作答</span>
                </div>
              </>
            )}
          </div>
        )}
      </motion.div>

      {/* 参考答案区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('reference')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-900">参考答案</span>
          </div>
          {expandedSections.reference ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.reference && (
          <div className="p-4">
            {hasReferenceData ? (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">参考实现说明</h4>
                  <QuestionHtmlWithZoom
                    html={questionDetail?.referenceSolution || question.referenceAnswer || '<p>暂无参考答案说明</p>'}
                  />
                </div>
              </div>
            ) : (
              <EmptyHint icon={CheckCircle} text="暂无参考答案" />
            )}
          </div>
        )}
      </motion.div>

      {/* 评分规则区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => toggleSection('rules')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-gray-900">评分规则</span>
          </div>
          {expandedSections.rules ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.rules && (
          <div className="p-4">
            {hasRulesData ? (
              <div className="space-y-4">
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <h4 className="font-medium text-indigo-900 mb-2">评分要点</h4>
                  <QuestionHtmlWithZoom
                    html={questionDetail?.scoringPoints || question.scoringPoints || '<p>暂无评分规则说明</p>'}
                  />
                </div>
              </div>
            ) : (
              <EmptyHint icon={Target} text="暂无评分规则" />
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
